/* eslint-disable import/extensions */

//
// Landing page content data for Balance Point Studios
// -------------------------------------------------------------
// Existing template image filenames are reused as placeholders.
// They are swapped for BPS-branded artwork in a later phase.
// Export names (Achievements, Portfolios, Advantages, Testimonials,
// TeamMembers) are preserved so existing component imports keep working.
//

import { PROGRAM_SLUGS } from '../constants';

// Visual placeholders mapped to nonprofit concepts:
//   Web.png      -> "Mission"        (icon-style image)
//   Mobile.png   -> "ProgramsIcon"   (renamed to avoid clash with Programs export)
//   Design.png   -> "Community"
import Mission from '../assets/images/Services/Web.png';
import ProgramsIcon from '../assets/images/Services/Mobile.png';
import Community from '../assets/images/Services/Design.png';

// Program preview / showcase artwork (placeholder)
import ArtisticImg from '../assets/images/Portfolio/Recruiting.png';
import MentorshipImg from '../assets/images/Portfolio/Stream.png';
import GrowthImg from '../assets/images/Portfolio/Freelance.png';

// "Why us" icons mapped to nonprofit strengths
import CommunityIcon from '../assets/images/Advantages/Communicative.png';
import MentorIcon from '../assets/images/Advantages/Collaborative.png';
import ProgramIcon from '../assets/images/Advantages/Management.png';
import HeartIcon from '../assets/images/Advantages/Favorite.png';

// Testimonial avatars (placeholder until real photos collected)
import TestimonialA from '../assets/images/Testimonials/Sasha.jpg';
import TestimonialB from '../assets/images/Testimonials/Reiner.jpg';
import TestimonialC from '../assets/images/Testimonials/Kruger.jpg';

// Team avatars (placeholder; swap with real Allison Black photo in Phase 8)
import FounderAvatar from '../assets/images/TeamMembers/CEO.jpg';
import MentorAvatarA from '../assets/images/TeamMembers/HRD.jpg';
import MentorAvatarB from '../assets/images/TeamMembers/Finance.jpg';
import MentorAvatarC from '../assets/images/TeamMembers/Project-manager.jpg';
import MentorAvatarD from '../assets/images/TeamMembers/Frontend1.jpg';
import MentorAvatarE from '../assets/images/TeamMembers/Frontend2.jpg';

// Impact stats shown on the landing page Achievement strip.
// Replace numeric milestones with verifiable values once available.
export const Achievements = [
  {
    title: '2023',
    subtitle: 'Founded in Cleveland',
    imageUrl: Mission,
    animation: 'left',
  },
  {
    title: '3',
    subtitle: 'Pillars of programming',
    imageUrl: ProgramsIcon,
    animation: 'up',
  },
  {
    title: 'Mt. Pleasant',
    subtitle: 'Neighborhood served',
    imageUrl: Community,
    animation: 'right',
  },
];

// "Portfolios" name is preserved because Portfolio.js, AllPortfolio.js
// and PortfolioDetail.js consume it directly. For Balance Point Studios
// each entry represents either a program or a community showcase.
export const Portfolios = [
  {
    id: PROGRAM_SLUGS.ARTISTIC,
    title: 'Artistic Instruction',
    imageUrl: ArtisticImg,
    type: 'Program',
    responsibility: ['Visual Arts', 'Music', 'Performance'],
    credit: '',
    description: 'Hands-on instruction in visual arts, music, and performance. Our studios are spaces where curiosity is rewarded, mistakes are part of the work, and every voice gets room to grow.',
  },
  {
    id: PROGRAM_SLUGS.MENTORSHIP,
    title: 'Mentorship & Guidance',
    imageUrl: MentorshipImg,
    type: 'Program',
    responsibility: ['1-on-1 Pairing', 'Life Navigation'],
    credit: '',
    description: 'Every young person we work with is paired with a mentor who shows up consistently — someone who knows their name, their story, and their goals.',
  },
  {
    id: PROGRAM_SLUGS.PERSONAL,
    title: 'Personal Development',
    imageUrl: GrowthImg,
    type: 'Program',
    responsibility: ['Self-Discovery', 'Leadership', 'Life Skills'],
    credit: '',
    description: 'Self-discovery is the heart of our work. Workshops on life skills, leadership, and self-reflection give youth the tools to redefine their future.',
  },
  {
    id: 'spring-2026-showcase',
    title: 'Spring 2026 Studio Showcase',
    imageUrl: ArtisticImg,
    type: 'Showcase',
    responsibility: ['Visual Arts', 'Performance'],
    credit: '',
    description: 'Our youth artists share work created across the spring cohort — open to family, friends, and the Mt. Pleasant community.',
  },
  {
    id: 'mentor-cohort-2026',
    title: '2026 Mentor Cohort',
    imageUrl: MentorshipImg,
    type: 'Showcase',
    responsibility: ['Mentorship'],
    credit: '',
    description: 'Welcoming a new cohort of volunteer mentors trained to walk alongside our youth for the program year.',
  },
  {
    id: 'leadership-lab-2026',
    title: 'Leadership Lab',
    imageUrl: GrowthImg,
    type: 'Showcase',
    responsibility: ['Personal Development'],
    credit: '',
    description: 'Six weeks of leadership exercises culminating in youth-led community projects.',
  },
];

// Convenience alias used by new pages (Programs index, Discover, etc.).
// Same data as Portfolios — we expose both names so old template
// components keep working while new code reads the friendlier name.
export const Programs = Portfolios.filter((item) => item.type === 'Program');
export const Showcases = Portfolios.filter((item) => item.type === 'Showcase');

// "Why us" — the four reasons families and partners choose Balance Point.
// The Advantage.js component renders this 2D array as two side-by-side rows.
export const Advantages = [
  [
    {
      title: 'Community-Rooted',
      description: 'Programming designed with and for Mt. Pleasant — by neighbors who plan to stay.',
      imageUrl: CommunityIcon,
    },
    {
      title: 'Mentor-First',
      description: 'Every young person is paired with a consistent mentor — relationships are our foundation.',
      imageUrl: MentorIcon,
    },
  ],
  [
    {
      title: 'Free for Youth',
      description: 'No fees, no barriers. Donors and volunteers make it possible for families to participate at no cost.',
      imageUrl: ProgramIcon,
    },
    {
      title: 'Built for Belonging',
      description: 'Studios where young people are seen, heard, and trusted to take creative risks.',
      imageUrl: HeartIcon,
    },
  ],
];

// Testimonials shown on the landing page. Replace placeholders with
// real quotes from youth, mentors, and families when collected.
// TODO: replace with verified testimonials after release form is signed.
export const Testimonials = [
  {
    id: 1,
    name: 'Youth Participant',
    company: 'Studio Member',
    testimoni: 'The studio is the first place that asked me what I wanted to make — not what I was going to do. That changed everything.',
    imageUrl: TestimonialA,
  },
  {
    id: 2,
    name: 'Volunteer Mentor',
    company: 'Mentor',
    testimoni: 'Mentoring here has reminded me why I do this work. Showing up matters more than I knew.',
    imageUrl: TestimonialB,
  },
  {
    id: 3,
    name: 'Mt. Pleasant Parent',
    company: 'Family',
    testimoni: 'My daughter comes home with new questions, new ideas, and a different look in her eye. That is everything.',
    imageUrl: TestimonialC,
  },
];

// Founder is real (Allison Black, statutory agent on the Articles of
// Incorporation). The remaining slots are placeholder roles for future
// hires; swap or remove once positions are filled.
export const TeamMembers = [
  {
    name: 'Allison Black',
    position: 'Founder & Director',
    imageUrl: FounderAvatar,
  },
  {
    name: 'Open role',
    position: 'Lead Mentor',
    imageUrl: MentorAvatarA,
  },
  {
    name: 'Open role',
    position: 'Programs Coordinator',
    imageUrl: MentorAvatarB,
  },
  {
    name: 'Open role',
    position: 'Community Liaison',
    imageUrl: MentorAvatarC,
  },
  {
    name: 'Open role',
    position: 'Volunteer Mentor',
    imageUrl: MentorAvatarD,
  },
  {
    name: 'Open role',
    position: 'Volunteer Mentor',
    imageUrl: MentorAvatarE,
  },
];

// Partner organizations supporting our work. Empty for v1 — populate
// after partnerships are confirmed.
// TODO: add real partner logos and names when confirmed.
export const Partners = [];
